import { MEMBER_COUNT_CAP } from './filters';
import { formatNumber } from './formatters';

/* A seed is the union of two capped pools: the brand's own top customers by spend, and the prospects that best match the rules.
   The brand share is the brand pool's percent of the seed size. */

/** The share proxima-audiences stores for a definition created without one. */
export const DEFAULT_BRAND_SHARE_PERCENT = 20;

export const BRAND_SHARE_ERROR = 'Enter a whole number from 0 to 100';

/** Whether `percent` is a share proxima-audiences accepts: a whole number from 0 to 100. */
export const isValidBrandShare = (percent: number | null): percent is number =>
  percent != null && Number.isInteger(percent) && percent >= 0 && percent <= 100;

/** The most people each pool can add to a seed. */
export type SeedSplit = { seedSize: number; brandLimit: number; prospectLimit: number };

/**
 * Splits a seed the way the proxima-audiences compiler does.
 *
 * - The seed size is `uniqueIndividualLimit`, or `MEMBER_COUNT_CAP` when no size cap is set.
 * - The brand pool's limit is `percent` of the seed size, rounded down.
 * - The prospect pool's limit is the rest of the seed size.
 */
export const seedSplit = (uniqueIndividualLimit: number | null, percent: number): SeedSplit => {
  const seedSize = uniqueIndividualLimit ?? MEMBER_COUNT_CAP;
  const brandLimit = Math.floor((seedSize * percent) / 100);
  return { seedSize, brandLimit, prospectLimit: seedSize - brandLimit };
};

/** The split as one sentence, such as "Up to 8,000 brand customers and 2,000 prospects in a 10,000 seed." */
export const describeSeedSplit = ({ seedSize, brandLimit, prospectLimit }: SeedSplit): string =>
  `Up to ${formatNumber(brandLimit)} brand customers and ${formatNumber(prospectLimit)} prospects in a ${formatNumber(seedSize)} seed.`;
